import React, { useEffect, useRef, useState } from "react";
import FormTable from "@/components/tables/FormTable";
import TableHead from "@/components/tables/TableHead";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import { fetchForms, transferBulkFormOwnership, type FormListType } from "@/services/form.service";
import { useDataQueries, normalizeSearchItem } from "@/utils/hooks/useDataQueries";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import {
    buildTransferOwnershipPayload,
    useOwnershipSelection,
} from "@/utils/hooks/useOwnershipSelection";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { useWindowInfiniteScroll } from "@/utils/hooks/useWindowInfiniteScroll";
import {
    getListCacheBootstrap,
    invalidateListCacheByPage,
    invalidateOwnershipTransferListCaches,
    writeListCache,
} from "@/utils/listPageCache";
import { GLOBAL_ORGANIZATION } from "@/utils/constants/appConstants/commonConstants";
import { FORM_LIST_TYPE_KEY, FormType } from "@/utils/constants/formTypeConstants";
import {
    BULK_TRANSFER_OWNERSHIP_QUEUED,
    BULK_TRANSFER_OWNERSHIP_SUCCESS,
} from "@/utils/constants/messages";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";

type Props = {
    formType?: FormListType;
};

type FormListState = {
    pageNo: number;
    forms: any[];
    formHistory: any[];
    formsLeft: boolean;
    allFetched: boolean;
    totalCount: number;
};

const emptyListState = (): FormListState => ({
    pageNo: 1,
    forms: [],
    formHistory: [],
    formsLeft: true,
    allFetched: false,
    totalCount: 0,
});

const listStateFromBootstrap = (items: any[], totalCount: number): FormListState => {
    const hasMore = items.length < totalCount;
    return {
        pageNo: 1,
        forms: items,
        formHistory: items,
        formsLeft: hasMore,
        allFetched: !hasMore,
        totalCount,
    };
};

function FormList({ formType = 'pdf' }: Props) {
    const {
        pageTitle,
        cachePageId,
        detailsPath,
        tableId,
        transferTitle,
        transferDescription,
        bulkTransferDescription,
    } = FormType[FORM_LIST_TYPE_KEY[formType]];
    const [
        ,
        ,
        dataLength,
        setDataLength,
        sorting,
        setSorting,
        filter,
        setFilter,
        searchItem,
        setSearchItem,
        resetDataQueries,
    ] = useDataQueries();
    const isCompactLayout = useCompactListLayout();
    const selection = useOwnershipSelection();
    const [showBulkTransferModal, setShowBulkTransferModal] = useState(false);
    const [bulkTransferLoading, setBulkTransferLoading] = useState(false);
    const isTransferFormOwnershipPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalTransferFormOwnership);
    const showTransferActions =
        isTransferFormOwnershipPermitted &&
        getCurrentOrganization()?.name !== GLOBAL_ORGANIZATION;

    const [appliedFilter, setAppliedFilter] = useState(filter);

    const listBootstrap = getListCacheBootstrap(cachePageId, {
        dataLength,
        sorting,
        searchItem,
        filter: appliedFilter,
    });

    const [listState, setListState] = useState<FormListState>(() =>
        listStateFromBootstrap(listBootstrap.items, listBootstrap.totalCount)
    );
    const { pageNo, forms, formsLeft, allFetched, totalCount } = listState;

    const [loadData, setLoadData] = useState(!listBootstrap.hasCache);
    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const loadMoreLockRef = useRef(false);

    const setPageNo = (nextPageNo: number) => {
        setListState((prev) => ({ ...prev, pageNo: nextPageNo }));
    };

    const setForms = (nextForms: any[] | ((prev: any[]) => any[])) => {
        setListState((prev) => ({
            ...prev,
            forms: typeof nextForms === "function" ? nextForms(prev.forms) : nextForms,
        }));
    };

    const fetchFormList = (
        page = 1,
        currentFilter = appliedFilter,
        currentSearch = searchItem
    ) => {
        const isInitialLoad = page === 1;
        const normalizedSearch = normalizeSearchItem(currentSearch);
        const { cacheKey, cached } = getListCacheBootstrap(cachePageId, {
            dataLength,
            sorting,
            searchItem: normalizedSearch,
            filter: currentFilter,
        });

        if (isInitialLoad && cached) {
            const cachedItems = cached.items as any[];
            setListState(listStateFromBootstrap(cachedItems, cached.totalCount));
            setLoadData(false);
            setIsLoading(false);
            loadMoreLockRef.current = false;
            return;
        }

        if (isInitialLoad) {
            setLoadData(true);
        }
        setIsLoading(true);

        fetchForms(
            page,
            dataLength,
            sorting,
            currentFilter,
            normalizedSearch,
            (response: any) => {
                const rawForms = response?.data?.forms ?? [];
                // API already filters by type; keep a defensive match for mixed payloads.
                const nextForms = rawForms.filter((form: any) => {
                    const formTypeValue = String(form?.type || 'pdf').toLowerCase();
                    return formTypeValue === formType;
                });
                const reportedTotal = response?.data?.total_count;

                setListState((prev) => {
                    const forms = page === 1 ? nextForms : [...prev.forms, ...nextForms];
                    const total =
                        reportedTotal ??
                        (nextForms.length >= dataLength
                            ? Math.max(prev.totalCount, forms.length + 1)
                            : forms.length);
                    const hasMore =
                        nextForms.length > 0 &&
                        (reportedTotal != null
                            ? forms.length < reportedTotal
                            : nextForms.length >= dataLength);
                    return {
                        ...prev,
                        totalCount: total,
                        forms,
                        formHistory: page === 1 ? nextForms : [...prev.formHistory, ...nextForms],
                        formsLeft: hasMore,
                        allFetched: !hasMore,
                    };
                });

                if (page === 1) {
                    writeListCache(
                        cacheKey,
                        nextForms,
                        reportedTotal ?? nextForms.length
                    );
                }

                setLoadData(false);
                setIsLoading(false);
                loadMoreLockRef.current = false;
            },
            (error: any) => {
                console.log(error);
                setLoadData(false);
                setIsLoading(false);
                loadMoreLockRef.current = false;
            },
            formType,
        );
    };

    useEffect(() => {
        // Always reload when form type changes (pdf vs web) so the two sections never share state.
        selection.clear();
        setListState(emptyListState());
        setLoadData(true);
        setIsLoading(true);

        if (listBootstrap.hasCache) {
            setListState(listStateFromBootstrap(listBootstrap.items, listBootstrap.totalCount));
            setLoadData(false);
            setIsLoading(false);
            return;
        }

        fetchFormList(1, appliedFilter, searchItem);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formType]);

    useWindowInfiniteScroll({
        enabled: !allFetched && !isLoading && formsLeft,
        onLoadMore: () => {
            if (loadMoreLockRef.current) {
                return;
            }
            loadMoreLockRef.current = true;
            const nextPage = Number(pageNo) + 1;
            setPageNo(nextPage);
            fetchFormList(nextPage, appliedFilter, searchItem);
        },
    });

    const search = (searchText: string) => {
        const nextSearch = normalizeSearchItem(searchText);
        setSearchItem(nextSearch);
        setListState((prev) => ({
            ...prev,
            pageNo: 1,
            formsLeft: true,
            allFetched: false,
        }));
        fetchFormList(1, appliedFilter, nextSearch);
    };

    const reset = () => {
        resetDataQueries?.();
        setAppliedFilter({});
        setListState(emptyListState());
        setLoadData(true);
        setIsLoading(true);
        selection.clear();
        fetchFormList(1, {}, null);
    };

    const handleApplyFilter = (filterOverride?: any) => {
        const nextFilter = filterOverride ?? filter;

        invalidateListCacheByPage(cachePageId);
        setListState((prev) => ({
            ...prev,
            pageNo: 1,
            forms: [],
            formHistory: [],
            formsLeft: true,
            allFetched: false,
        }));
        setFilter(nextFilter);
        setAppliedFilter(nextFilter);
        fetchFormList(1, nextFilter, searchItem);
    };

    const formIds = forms.map((form) => form.id).filter(Boolean);

    const reloadForms = () => {
        invalidateOwnershipTransferListCaches("form");
        setListState((prev) => ({
            ...prev,
            pageNo: 1,
            forms: [],
            formHistory: [],
            formsLeft: true,
            allFetched: false,
        }));
        setLoadData(true);
        setIsLoading(true);
        fetchFormList(1, appliedFilter, searchItem);
    };

    const handleBulkTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || selection.selectedIds.length === 0) return;

        setBulkTransferLoading(true);
        try {
            const payload = buildTransferOwnershipPayload(selectedUser);
            const result = await transferBulkFormOwnership({
                form_ids: selection.selectedIds,
                ...payload,
            });
            selection.clear();
            setShowBulkTransferModal(false);
            successToasterMessage(
                result?.is_async
                    ? BULK_TRANSFER_OWNERSHIP_QUEUED
                    : BULK_TRANSFER_OWNERSHIP_SUCCESS,
            );
            reloadForms();
        } catch (error) {
            console.log("Error bulk transferring form ownership:", error);
            errorToasterMessage(error);
        } finally {
            setBulkTransferLoading(false);
        }
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id="form-table"
                    handleReset={reset}
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    setFilter={setFilter}
                    filter={filter}
                    setDataLength={setDataLength as (dataLength: number) => void}
                    reset={reset}
                    search={search}
                    title={pageTitle}
                    currentEnvo={forms.length}
                    totalEnvo={totalCount}
                    handleApplyFilter={handleApplyFilter}
                    searchItem={searchItem as string}
                    selectedCount={showTransferActions ? selection.selectedIds.length : 0}
                    showMultiselectSelectAll={showTransferActions && isCompactLayout && formIds.length > 0}
                    selectAllChecked={
                        selection.areAllSelected(formIds)
                            ? true
                            : selection.areSomeSelected(formIds)
                              ? "indeterminate"
                              : false
                    }
                    onSelectAllChange={() => selection.toggleAll(formIds)}
                    multiselectAction={
                        showTransferActions ? (
                            <TransferOwnershipButton
                                id="form-table-bulk-transfer-btn"
                                iconId="form-table-bulk-transfer-icon"
                                className="gs-transfer-ownership-btn--toolbar"
                                onClick={() => setShowBulkTransferModal(true)}
                            />
                        ) : null
                    }
                />
            </div>
            <FormTable
                id={tableId}
                reset={reset}
                title={pageTitle}
                setForms={setForms}
                search={search}
                isLoading={isLoading}
                dataLength={dataLength as number}
                sorting={sorting}
                setSorting={setSorting as (sorting: any) => void}
                searchItem={searchItem as string}
                allFetched={allFetched}
                forms={forms}
                dataLoad={loadData}
                resetDataQueries={reloadForms}
                setPageNo={setPageNo}
                loadingData={isLoading}
                selection={selection}
                showTransferActions={showTransferActions}
                detailsPath={detailsPath}
                transferTitle={transferTitle}
                transferDescription={transferDescription}
            />
            {showBulkTransferModal ? (
                <TransferOwnershipModal
                    show={showBulkTransferModal}
                    onHide={() => setShowBulkTransferModal(false)}
                    onTransferOwnership={handleBulkTransferOwnership}
                    loading={bulkTransferLoading}
                    title={transferTitle}
                    description={bulkTransferDescription}
                />
            ) : null}
        </div>
    );
}

export default FormList;
